import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle, WarningCircle } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PublicForm() {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [err, setErr] = useState("");
  const [loadErr, setLoadErr] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [region, setRegion] = useState("");
  const [regions, setRegions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND}/api/forms/public/${slug}`)
      .then((r) => setForm(r.data))
      .catch((e) => setLoadErr(e?.response?.data?.detail || "Form not found or inactive"));
    // Public settings — regions list is safe to expose read-only via portal helper; we fetch our own regions list from settings via a minimal read
    axios.get(`${BACKEND}/api/portal/regions`).then((r) => setRegions(r.data || [])).catch(() => {});
  }, [slug]);

  if (loadErr) return <PublicShell><div className="flex items-center gap-2 text-red-600"><WarningCircle size={20} weight="duotone" /> {loadErr}</div></PublicShell>;
  if (!form) return <PublicShell><div className="text-slate-500 text-sm">Loading…</div></PublicShell>;

  if (done) return (
    <PublicShell>
      <div className="text-center py-6 space-y-4" data-testid="form-success">
        <CheckCircle size={56} weight="duotone" className="mx-auto text-emerald-500" />
        <h1 className="font-heading font-bold text-2xl">Your request has been submitted</h1>
        <p className="text-slate-600">Ticket #{done.ticket_number} is now with the Business Systems Support team. You&apos;ll get an email confirmation shortly.</p>
        <div className="border-t border-slate-100 pt-4 mt-2 text-sm text-slate-600">
          Want to see all your past requests too?{" "}
          <a
            href="/portal"
            className="text-[#4f4c90] hover:underline font-medium"
            data-testid="form-success-portal-link"
          >
            Track my requests →
          </a>
          <div className="text-[11px] text-slate-500 font-mono mt-1">
            Sign in with the same email you just used and we&apos;ll show you every ticket & submission tied to it.
          </div>
        </div>
      </div>
    </PublicShell>
  );

  const setA = (fid, v) => setAnswers((cur) => ({ ...cur, [fid]: v }));

  const submit = async (e) => {
    e.preventDefault();
    for (const fld of form.fields) {
      const v = answers[fld.id];
      // §form-builder — required-answer check. Type-explicit branch
      // for Yes/No (checkbox) because Boolean `false` is a real answer
      // (must NOT be treated as "empty"). Everything else uses the
      // generic empty-value shape.
      let has;
      if (fld.field_type === "checkbox") {
        has = v === true || v === false;
      } else {
        has = v !== undefined && v !== null && v !== "" && (!Array.isArray(v) || v.length > 0);
      }
      if (fld.required && !has && !fld.fillable_later) { setErr(`"${fld.label}" is required`); return; }
    }
    setErr("");
    setSubmitting(true);
    try {
      const payload = {
        // Feature A — subject is now admin-set on the form. Backend
        // resolves it from form.ticket_subject (fallback to form.title).
        submitter_email: email,
        submitter_name: name,
        region: region || null,
        answers: form.fields.map((f) => ({ field_id: f.id, label: f.label, value: answers[f.id] ?? null })),
      };
      const r = await axios.post(`${BACKEND}/api/forms/public/${slug}/submit`, payload);
      setDone(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Submit failed");
    } finally { setSubmitting(false); }
  };

  return (
    <PublicShell>
      <div className="space-y-1 mb-6">
        <div className="text-[11px] uppercase tracking-wider text-[#4f4c90] font-medium">{form.issue_type}</div>
        <h1 className="font-heading font-bold text-2xl">{form.title}</h1>
        {form.description && <p className="text-sm text-slate-600 whitespace-pre-wrap">{form.description}</p>}
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Your name</Label><Input required value={name} onChange={(e) => setName(e.target.value)} className="mt-1 bg-white border-slate-200" data-testid="submitter-name" /></div>
          <div><Label className="text-xs">Work email</Label><Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 bg-white border-slate-200" data-testid="submitter-email" /></div>
        </div>
        {regions.length > 0 && (
          <div>
            <Label className="text-xs">Region</Label>
            <Select value={region || "__none__"} onValueChange={(v) => setRegion(v === "__none__" ? "" : v)}>
              <SelectTrigger className="mt-1 bg-white border-slate-200"><SelectValue placeholder="Select region…" /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200"><SelectItem value="__none__">— none —</SelectItem>{regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {form.fields.map((fld) => (
          <div key={fld.id}>
            <Label className="text-xs flex items-center gap-2">
              <span>{fld.label} {fld.required && !fld.fillable_later && <span className="text-red-500">*</span>}</span>
              {fld.fillable_later && (
                <span className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 font-medium">
                  Optional
                </span>
              )}
            </Label>
            {fld.help_text && <p className="text-[11px] text-slate-500 mb-1">{fld.help_text}</p>}
            {fld.fillable_later && !fld.help_text && (
              <p className="text-[11px] text-slate-500 mb-1 italic">You can fill this in now or add it later from your portal.</p>
            )}
            {fld.field_type === "short_text" && <Input value={answers[fld.id] || ""} onChange={(e) => setA(fld.id, e.target.value)} className="mt-1 bg-white border-slate-200" />}
            {fld.field_type === "long_text" && <Textarea value={answers[fld.id] || ""} onChange={(e) => setA(fld.id, e.target.value)} className="mt-1 bg-white border-slate-200 min-h-20" />}
            {fld.field_type === "email" && <Input type="email" value={answers[fld.id] || ""} onChange={(e) => setA(fld.id, e.target.value)} className="mt-1 bg-white border-slate-200" />}
            {fld.field_type === "date" && <Input type="date" value={answers[fld.id] || ""} onChange={(e) => setA(fld.id, e.target.value)} className="mt-1 bg-white border-slate-200" />}
            {fld.field_type === "select" && (
              <Select value={answers[fld.id] || ""} onValueChange={(v) => setA(fld.id, v)}>
                <SelectTrigger className="mt-1 bg-white border-slate-200"><SelectValue placeholder="Choose…" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">{(fld.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {fld.field_type === "multi_select" && (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {(fld.options || []).map((o) => {
                  const cur = answers[fld.id] || [];
                  const on = cur.includes(o);
                  return (
                    <button key={o} type="button" onClick={() => setA(fld.id, on ? cur.filter((x) => x !== o) : [...cur, o])}
                      className={`text-xs px-3 py-1.5 rounded-md border ${on ? "bg-[#4f4c90] text-white border-[#4f4c90]" : "bg-white text-slate-600 border-slate-200"}`}>{o}</button>
                  );
                })}
              </div>
            )}
            {fld.field_type === "checkbox" && (
              // §form-builder — labelled "Yes / No" in the builder, so
              // render two radios not a single checkbox. Value stays
              // boolean (true = Yes, false = No) for back-compat with
              // existing submissions. Undefined until user picks so
              // required-validation can catch an untouched field.
              <div className="mt-1 flex items-center gap-4 text-sm text-slate-800" data-testid={`yesno-${fld.id}`}>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name={`yn-${fld.id}`}
                    checked={answers[fld.id] === true}
                    onChange={() => setA(fld.id, true)}
                    data-testid={`yesno-${fld.id}-yes`}
                  />
                  Yes
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name={`yn-${fld.id}`}
                    checked={answers[fld.id] === false}
                    onChange={() => setA(fld.id, false)}
                    data-testid={`yesno-${fld.id}-no`}
                  />
                  No
                </label>
              </div>
            )}
          </div>
        ))}
        {err && <div className="text-sm text-red-600 flex items-center gap-1"><WarningCircle size={14} /> {err}</div>}
        <Button type="submit" disabled={submitting} className="w-full bg-[#4f4c90] hover:bg-[#5F5CA5] h-11" data-testid="form-submit">{submitting ? "Submitting…" : "Submit"}</Button>
      </form>
    </PublicShell>
  );
}

function PublicShell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-start justify-center p-6">
      <div className="w-full max-w-xl bg-white rounded-lg hairline p-6 mt-6">{children}</div>
    </div>
  );
}
